'use client';

import type { IdentifyOptions, ProdantixClient } from '@prodantix/sdk';
import { type ChatHandle, mountChat } from '@prodantix/sdk/chat';
import { cachedMessengerConfig, fetchMessengerConfig } from '@prodantix/sdk/messenger';
import { createWebClient, type WebClientOptions } from '@prodantix/sdk/web';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';

export type { WebClientOptions };

export type PersonTraits = NonNullable<IdentifyOptions['traits']>;

export interface Person {
  distinctId: string;
  traits?: PersonTraits;
}

export interface ProdantixOptions extends WebClientOptions {
  /** The public browser edge, e.g. `https://eu.edge.prodantix.com`. The
   *  messenger appears when this is set AND the project has it switched on in
   *  the console; absent, the provider installs analytics only. */
  messengerHost?: string;
  /**
   * The signed-in person, as the surface's session knows them: the token's
   * `sub`. A prop rather than a call, so sign-in and sign-out reach the client
   * as the values they are. Three states, and the surface owes the right one:
   *
   * `undefined` means the surface does not know yet, and nothing happens. A
   * mount that consults no session at all passes this too: `null` is a claim
   * that a session was consulted and found empty, and a hand-passed `null` from
   * a page with no session would reset a person who is still signed in
   * elsewhere on the site, rotating their anonymous id mid-session.
   *
   * A value identifies, on the first value and whenever the distinct id
   * changes.
   *
   * `null` means the surface KNOWS nobody is signed in, and resets the client if
   * it still holds someone, which rotates the anonymous id so the next visitor
   * on this browser is not the person who signed out. It must never stand for
   * "loading": sign-out on a BFF surface is a hard navigation, so the reset has
   * to fire on a first render with `null`, and a signed-in person reloading a
   * page would be reset before their session hydrated.
   */
  person?: Person | null;
}

export interface ProdantixProviderProps {
  children?: ReactNode;
  options: ProdantixOptions;
}

export interface MessengerHandle {
  open(): void;
  close(): void;
}

const ProdantixContext = createContext<ProdantixClient | null>(null);
const MessengerContext = createContext<MessengerHandle | null>(null);

/** Prefixed so a reader can tell which package went quiet. */
const PREFIX = '[prodantix/react]';

interface ActiveConfig {
  apiKey: string;
  host: string;
}

/** The web-client config when both public values are present, else null: an app
 *  ships identically with prodantix disabled (no key in the build). */
export function prodantixConfig(env: { host?: string; key?: string }): ActiveConfig | null {
  const apiKey = env.key?.trim() ?? '';
  const host = env.host?.trim() ?? '';
  if (apiKey === '' || host === '') {
    return null;
  }
  return { apiKey, host };
}

/**
 * Why this mount will send nothing, or null when it will.
 *
 * Every reason is reported, never a silent return: an app that mounts the
 * provider and ships no events looks exactly like an app that is working,
 * because the component renders null either way and the beacon is the only
 * evidence.
 */
export function skipReason(env: { host?: string; key?: string }, active: ActiveConfig | null): string | null {
  const config = prodantixConfig(env);
  if (config === null) {
    const missing = [env.key?.trim() ? null : 'key', env.host?.trim() ? null : 'host'].filter(Boolean).join(' and ');
    return `${PREFIX} disabled: no ${missing}. Nothing will be sent from this app.`;
  }
  if (active === null) {
    return null;
  }
  /* Installing twice would double every event and attach a second set of
     lifecycle listeners, so the first install wins. A CHANGED config after that
     is a real change that will not take effect, which is worth saying. */
  if (active.apiKey !== config.apiKey || active.host !== config.host) {
    return `${PREFIX} already installed against ${active.host}; the new host ${config.host} is ignored for this page load.`;
  }
  return null;
}

/** One client per page, not per provider: React 18 StrictMode mounts twice, and
 *  `createWebClient` patches the History API and installs lifecycle listeners,
 *  so a second creation would double every autocaptured event. The messenger
 *  needs the same guard for the same reason. */
let active: ActiveConfig | null = null;
let messenger: ChatHandle | null = null;
let sharedClient: ProdantixClient | null = null;

/** Test seam: the module-level install is deliberately once-per-page-load, so a
 *  test needs a way back to a clean slate. */
export function resetProdantixForTest(): void {
  active = null;
  sharedClient = null;
  messenger?.destroy();
  messenger = null;
}

export const ProdantixProvider = ({ children, options }: ProdantixProviderProps) => {
  const [client, setClient] = useState<ProdantixClient | null>(sharedClient);
  const [handle, setHandle] = useState<MessengerHandle | null>(null);
  const person = options.person;
  const identified = useRef<string | null>(null);

  useEffect(() => {
    const env = { host: options.host, key: options.apiKey };
    const reason = skipReason(env, active);
    if (reason !== null) {
      console.warn(reason);
      return;
    }
    const config = prodantixConfig(env);
    if (config === null || active !== null) {
      setClient(sharedClient);
      return;
    }
    try {
      sharedClient = createWebClient(options);
      // Marked installed only once the client exists. Set before the call, a
      // throw left the module claiming an install that never happened, so no
      // later mount would ever retry it.
      active = config;
      setClient(sharedClient);
    } catch (error) {
      console.error(`${PREFIX} failed to start against ${config.host}:`, error);
    }
  }, [options]);

  useEffect(() => {
    if (client === null || person === undefined) {
      return;
    }
    if (person === null) {
      // The client's own persisted state, not this mount's memory of it: a
      // sign-out navigates hard, so the reset falls to a first render that
      // never saw the person identified.
      if (!client.isIdentified) {
        return;
      }
      client.reset();
      identified.current = null;
      return;
    }
    if (identified.current === person.distinctId) {
      return;
    }
    client.identify(person.distinctId, { traits: person.traits });
    identified.current = person.distinctId;
  }, [client, person]);

  useEffect(() => {
    const host = options.messengerHost?.trim();
    if (!host || !options.apiKey || messenger) {
      return;
    }
    let cancelled = false;
    const distinctId = sharedClient?.distinctId ?? 'anonymous';

    void (async () => {
      const config =
        (await fetchMessengerConfig({
          apiKey: options.apiKey,
          host,
          onError: error => console.warn(`${PREFIX} messenger settings could not be read:`, error),
        })) ?? cachedMessengerConfig(undefined, options.apiKey);

      // No config and no cache means nothing renders, and the console already
      // said why. A default-shaped launcher here would be a guess painted on a
      // customer's page.
      if (cancelled || !config?.enabled || messenger) {
        return;
      }
      messenger = mountChat({ apiKey: options.apiKey, appearance: config, distinctId, host });
      setHandle({ close: () => messenger?.close(), open: () => messenger?.open() });
    })();

    return () => {
      cancelled = true;
    };
  }, [options]);

  return (
    <ProdantixContext.Provider value={client}>
      <MessengerContext.Provider value={handle}>{children}</MessengerContext.Provider>
    </ProdantixContext.Provider>
  );
};

/** The page's client, or null on the server, before the provider's effect has
 *  run, or outside a provider. */
export const useProdantix = (): ProdantixClient | null => useContext(ProdantixContext);

/** The messenger, for a site that wants its own button to open it. Null until
 *  the project's configuration has been read and says enabled, so a caller must
 *  handle that rather than assume a handle exists. */
export const useMessenger = (): MessengerHandle | null => useContext(MessengerContext);
